import {
  DatabaseAuto,
  IcommerceUser,
  IdataArrayResponse,
  IfilterProps,
  IvendorCreditMemo,
  IvendorMemoAnalytics,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

/**
 * Type alias for vendor credit memo properties with specific generic types
 * @template T - Type for vendor credit memo (default: IvendorCreditMemo<number, IcommerceUser>)
 */
type TvendorCreditMemoProps = IvendorCreditMemo<number, IcommerceUser>;

/**
 * Represents a vendor credit memo in the system.
 * This class provides methods to manage and track vendor credit memos,
 * including creation, retrieval, updating, and filtering of credit memo records.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template TvendorCreditMemoProps - The type definition for vendor credit memo properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link VendorCreditMemo} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const vendorCreditMemo = await VendorCreditMemo.getAll();
 *
 * const vendorCreditMemo = await VendorCreditMemo.filterAll({
 *
 * });
 *
 * const vendorCreditMemo = await VendorCreditMemo.bulkDelete({
 *
 * });
 *
 * const vendorCreditMemo = await VendorCreditMemo.getOne('resourceId');
 *
 * const vendorCreditMemo = await VendorCreditMemo.remove();
 *
 * ```
 */
export class VendorCreditMemo extends DatabaseAuto {
  /** Base API endpoint for vendor credit memo operations */
  static readonly baseUrl = '/vendor-credit-memo';

  /** Unique reference ID for the vendor credit memo */
  readonly urId: TvendorCreditMemoProps['urId'];

  /** Current status of the vendor credit memo */
  readonly vendorCreditMemoStatus: TvendorCreditMemoProps['vendorCreditMemoStatus'];

  /** Unique number identifying the vendor credit memo */
  readonly vendorCreditMemoNumber: TvendorCreditMemoProps['vendorCreditMemoNumber'];

  /** Related vendor bill information */
  readonly vendorBill: TvendorCreditMemoProps['vendorBill'];

  /** Items included in the vendor credit memo */
  readonly vendorCreditMemoItems: TvendorCreditMemoProps['vendorCreditMemoItems'];

  /** Total amount of the credit memo */
  readonly amount: TvendorCreditMemoProps['amount'];

  /** Date of the credit memo */
  readonly date: TvendorCreditMemoProps['date'];

  /** Company associated with the vendor credit memo */
  readonly company: TvendorCreditMemoProps['company'];

  /** Date when the credit memo was issued */
  readonly dateOfIssue: TvendorCreditMemoProps['dateOfIssue'];

  /** Date when payment for the credit memo was initiated */
  readonly datePaymentInitiated: TvendorCreditMemoProps['datePaymentInitiated'];

  /** Date when payment for the credit memo was completed */
  readonly datePaymentCompleted: TvendorCreditMemoProps['datePaymentCompleted'];

  /** Payment metadata */
  readonly paymentMeta: TvendorCreditMemoProps['paymentMeta'];

  /**
   * Creates a new instance of VendorCreditMemo.
   * @param {TvendorCreditMemoProps} data - The vendor credit memo data
   * @throws {Error} If the data is invalid or missing required fields
   */
  constructor(data: TvendorCreditMemoProps) {
    validateData(data, {
      urId: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Unique reference ID must be a non-empty string'
      }
    });

    super(data);

    this.urId = data.urId;
    this.vendorCreditMemoStatus = data.vendorCreditMemoStatus;
    this.vendorCreditMemoNumber = data.vendorCreditMemoNumber;
    this.vendorBill = data.vendorBill;
    this.vendorCreditMemoItems = data.vendorCreditMemoItems;
    this.amount = data.amount;
    this.date = data.date;
    this.company = data.company;
    this.dateOfIssue = data.dateOfIssue;
    this.datePaymentInitiated = data.datePaymentInitiated;
    this.datePaymentCompleted = data.datePaymentCompleted;
    this.paymentMeta = data.paymentMeta;
  }

  /**
   * Retrieves analytics data for vendor credit memos.
   * @param {IfilterProps} filter - The filter criteria for the analytics query
   * @returns {Promise<IvendorMemoAnalytics>} The analytics result
   * @throws {Error} If the analytics request fails
   */
  static getVendorMemoAnalytics(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IvendorMemoAnalytics>(
        `${VendorCreditMemo.baseUrl}/analytics`,
        filter
      );

    return lastValueFrom(observer$);
  }

  /**
   * Retrieves all vendor credit memos with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: VendorCreditMemo[] }>}
   * An object containing the total count and an array of vendor credit memos
   * @throws {Error} If there's an error fetching the vendor credit memos
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();

    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TvendorCreditMemoProps>>(`${VendorCreditMemo.baseUrl}?${query}`);

    const vendorCreditMemos = await lastValueFrom(observer$);

    return {
      count: vendorCreditMemos.count,
      dataArr: vendorCreditMemos.data.map(val => new VendorCreditMemo(val))
    };
  }

  /**
   * Filters vendor credit memos based on specified criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: VendorCreditMemo[] }>}
   * An object containing the total count and an array of filtered vendor credit memos
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TvendorCreditMemoProps>>(
        `${VendorCreditMemo.baseUrl}/filter`,
        filter
      );

    const vendorCreditMemos = await lastValueFrom(observer$);

    return {
      count: vendorCreditMemos.count,
      dataArr: vendorCreditMemos.data.map(val => new VendorCreditMemo(val))
    };
  }

  /**
   * Retrieves a single vendor credit memo by its ID.
   * @param {string} resourceId - The unique identifier of the vendor credit memo to retrieve
   * @returns {Promise<VendorCreditMemo>} The requested vendor credit memo
   * @throws {Error} If the vendor credit memo is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TvendorCreditMemoProps>(`${VendorCreditMemo.baseUrl}/${resourceId}`);
    const vendorCreditMemo = await lastValueFrom(observer$);

    return new VendorCreditMemo(vendorCreditMemo);
  }

  /**
   * Updates the status of the current vendor credit memo.
   * @param {{ vendorCreditMemoStatus: TvendorCreditMemoProps['vendorCreditMemoStatus'] }} payload - The new status
   * for the vendor credit memo
   * @returns {Promise<VendorCreditMemo>} The updated vendor credit memo
   * @throws {Error} If the update operation fails or the vendor credit memo doesn't exist
   */
  async update(payload: { vendorCreditMemoStatus: TvendorCreditMemoProps['vendorCreditMemoStatus'] }) {
    const observer$ = CommerceClient.ehttp
      .makePut<TvendorCreditMemoProps>(
        `${VendorCreditMemo.baseUrl}/${this._id}`,
        payload
      );

    return new VendorCreditMemo(await lastValueFrom(observer$));
  }
}
